import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import Typography from '@material-ui/core/Typography';
import Backdrop from '@material-ui/core/Backdrop';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormSection from '../../form/FormSection';
import { NgpLabelValue } from '../../form/NgpLabelValue';

import AdminPage from '../AdminPage';
import { AdminPages, buildPageId } from '../AdminLinkUtils';
import AuthRoleGuard from '../../auth/AuthRoleGuard';
import Actions from '../../page-framework/actions/Actions';
import format, { DEFAULT_INVALID_VALUE, FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import useInterval from '../../../utilities/UseInterval';
import { useDataService } from '../../../services/ApiDataService';
import { useSystemConfigService } from '../../../services/SystemConfigService';
import useAlerts from '../../../stores/AlertsStore';

const functionName = AdminPages.ActiveStandbyPlms;

const LIST_URL = '/api/v3/standby/list';
const MAX_LOADING_INTERVAL = 120000;
const REFRESH_URL = '/api/v3/standby/refresh';
const REFRESH_DATA_INTERVAL = 30000;
const REFRESH_POLL_INTERVAL = 2000;
const STATUS_READY = 'normal';
const STATUS_NOT_READY = 'degraded';
const STATUS_CRITICAL = 'critical';

// Styles for status coloring
const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 100,
        opacity: 0.7,
        color: '#fff',
    },
    label: {
        borderRadius: 3,
        display: 'inline-block',
        padding: '2px 4px',
        marginLeft: '16px',
        fontWeight: 'bold',
        color: '#FFFFFF',
        verticalAlign: 'baseline',
        whiteSpace: 'nowrap',
        textShadow: '0 -1px 0 rgba(0, 0, 0, 0.25)',
        width: 'auto',
    },
    labelReady: {
        backgroundColor: theme.palette.status.green,
    },
    labelNotReady: {
        backgroundColor: theme.palette.alert.warning,
    },
    labelCritical: {
        backgroundColor: theme.palette.alert.error,
    },
    section: {
        display: 'flex',
        alignItems: 'center',
    },
    details: {
        marginLeft: '15px',
    },
}));

const NOT_ACTIVE_STANDBY = {
    data: [],
    total: 0,
    system: null,
};

function getStatusClass(classes, status) {
    switch (status) {
        case `${STATUS_NOT_READY}`:
            return clsx(classes.label, classes.labelNotReady);
        case `${STATUS_CRITICAL}`:
            return clsx(classes.label, classes.labelCritical);
        case `${STATUS_READY}`:
            return clsx(classes.label, classes.labelReady);
        default:
            return null;
    }
}

function getStandbyStatus(status) {
    switch (status) {
        case `${STATUS_NOT_READY}`:
            return t`Not Ready`;
        case `${STATUS_CRITICAL}`:
            return t`Critical`;
        case `${STATUS_READY}`:
            return t`Ready`;
        default:
            return DEFAULT_INVALID_VALUE;
    }
}

function useStandbySystemInterval() {
    const systemConfigService = useSystemConfigService();
    const [statusInterval, setStatusInterval] = useState(1);
    useEffect(() => {
        systemConfigService
            .getStandbyStatusInterval()
            .then((json) => setStatusInterval(json?.standbyStatusInterval || 1));
    }, [systemConfigService]);
    return statusInterval;
}

export default function ActiveStandbyPlmList() {
    const classes = useStyles();
    const [standbyData, setStandbyData] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingStart, setLoadingStart] = useState(0);
    const dataService = useDataService();
    const standbyStatusInterval = useStandbySystemInterval();
    const [, /* Not used */ alertsActions] = useAlerts();
    const { active, standby, lastSeen } = standbyData || {};

    // Periodically poll the standby/refresh route whenever the user has pressed Refresh, until
    // it returns a status of "ready" (or more than 2 minutes has elapsed).
    const checkRefresh = async () => {
        if (loading) {
            if (Date.now() - loadingStart > MAX_LOADING_INTERVAL) {
                setLoading(false);
                alertsActions.createWarningAlert(t`Timeout collecting active/standby status data`);
                return;
            }
            try {
                const json = await dataService.post(REFRESH_URL, {});
                if (json?.status === 'ready') {
                    setLoading(false);
                    await getData();
                }
            } catch (e) {
                setLoading(false);
                if (e?.responseBody?.error) {
                    alertsActions.createErrorAlert(e.responseBody.error);
                }
            }
        }
    };

    // Start refresh polling on the page
    const handleRefresh = () => {
        setLoading(true);
        setLoadingStart(Date.now());
    };

    // Get the most recent active/standby data
    const getData = async () => {
        let result;
        try {
            result = await dataService.get(LIST_URL);
        } catch (e) {
            result = NOT_ACTIVE_STANDBY;
            if (e?.responseBody?.error) {
                alertsActions.createErrorAlert(e.responseBody.error);
            }
        }
        setStandbyData(result);
    };

    // Refresh the data on the page every 30 seconds
    useInterval(() => getData(), REFRESH_DATA_INTERVAL, true);
    useInterval(() => checkRefresh(), REFRESH_POLL_INTERVAL);

    const { status: activeStatus, summary: activeSummary, message: activeMessage } = active || {};
    const {
        status: standbyStatus,
        summary: standbySummary,
        message: standbyMessage,
        replicationPct,
        estComplete,
    } = standby || {};

    if (!active) return null;

    const activeFormatName = format(active?.name, { type: FORMATTER_TYPES.TEXT });
    const activeFormatIp = format(active?.ipAddress, { type: FORMATTER_TYPES.TEXT });
    const activeFormatStatus = getStandbyStatus(activeStatus);
    const activeFormatMessage = format(activeMessage, { type: FORMATTER_TYPES.TEXT });
    const activeStatusClass = getStatusClass(classes, activeStatus);
    const standbyFormatName = format(standby?.name, { type: FORMATTER_TYPES.TEXT });
    const standbyFormatIp = format(standby?.ipAddress, { type: FORMATTER_TYPES.TEXT });
    const standbyFormatStatus = getStandbyStatus(standbyStatus);
    const standbyFormatMessage = format(standbyMessage, { type: FORMATTER_TYPES.TEXT });
    const standbyFormatEstComplete = format(estComplete, { type: FORMATTER_TYPES.DURATIONUNTIL, precision: 2 });
    const standbyFormatLastSeen = format(lastSeen, { type: FORMATTER_TYPES.DURATIONSINCE });
    const standbyStatusClass = getStatusClass(classes, standbyStatus);

    // Only show the "Replication Progress" and "Estimated Completion" properties if A) there is a message
    // set (indicating a non-ready condition), and B) it's working on replicating cassandra. Note that
    // estComplete will be "--" until replicationPct > 10, but we should show the % when we can.
    const showReplicationProperties = standbyMessage && replicationPct && replicationPct < 100;
    const showRefresh = standbyStatusInterval > 1;

    return (
        <AdminPage
            title={t`Active/Standby`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {showRefresh && (
                        <IconButton disabled={loading} title={t`Refresh`} onClick={handleRefresh}>
                            <RefreshOutlinedIcon />
                        </IconButton>
                    )}
                </Actions>
            }
        >
            <AuthRoleGuard allowedRoles={['admin', 'super']}>
                <Backdrop className={classes.backdrop} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <div className={classes.section}>
                    <Typography variant="h6">{t`Active Server`}</Typography>
                    <span className={classes.details}>{`(${activeFormatName})`}</span>
                </div>
                <FormSection>
                    <NgpLabelValue
                        dataLabel={t`Failover Status`}
                        dataValue={activeFormatStatus}
                        dataValueClassName={activeStatusClass}
                    />
                    <NgpLabelValue dataLabel={t`Summary`} dataValue={activeSummary} />
                    {activeMessage && <NgpLabelValue dataLabel={t`Detail`} dataValue={activeFormatMessage} />}
                    <NgpLabelValue dataLabel={t`IP Address`} dataValue={activeFormatIp} />
                </FormSection>
                {standby && (
                    <>
                        <div className={classes.section}>
                            <Typography variant="h6">{t`Standby Server`}</Typography>
                            <span className={classes.details}>{`(${standbyFormatName})`}</span>
                        </div>
                        <FormSection>
                            <NgpLabelValue
                                dataLabel={t`Failover Status`}
                                dataValue={standbyFormatStatus}
                                dataValueClassName={standbyStatusClass}
                            />
                            <NgpLabelValue dataLabel={t`Summary`} dataValue={standbySummary} />
                            {standbyMessage && <NgpLabelValue dataLabel={t`Detail`} dataValue={standbyFormatMessage} />}
                            {showReplicationProperties && (
                                <>
                                    <NgpLabelValue
                                        dataLabel={t`Replication Progress`}
                                        dataValue={`${replicationPct}%`}
                                    />
                                    <NgpLabelValue
                                        dataLabel={t`Estimated Completion`}
                                        dataValue={standbyFormatEstComplete}
                                    />
                                </>
                            )}
                            <NgpLabelValue dataLabel={t`Last Seen`} dataValue={standbyFormatLastSeen} />
                            <NgpLabelValue dataLabel={t`IP Address`} dataValue={standbyFormatIp} />
                        </FormSection>
                    </>
                )}
            </AuthRoleGuard>
        </AdminPage>
    );
}
